/**
 * ActionFeedback Component v1.0
 * 
 * Provides hybrid feedback system:
 * - Inline feedback for clarity and persistence
 * - Toast notifications for immediate reinforcement
 * 
 * Usage:
 * 1. Import the component and hook
 * 2. Use useActionFeedback() hook to trigger feedback
 * 3. Place <ActionFeedbackInline /> where you want inline feedback
 */

import React, { useState, useEffect, createContext, useContext } from 'react';
import { toast } from 'sonner';
import { Check, TrendingUp, Flame, Award, ArrowUp, Star, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// =============================================================================
// CONTEXT FOR MANAGING FEEDBACK STATE
// =============================================================================

const ActionFeedbackContext = createContext(null);

export const ActionFeedbackProvider = ({ children }) => {
  const [currentFeedback, setCurrentFeedback] = useState(null);
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  
  const showFeedback = (feedback) => {
    setCurrentFeedback(feedback);
    setFeedbackHistory(prev => [feedback, ...prev.slice(0, 9)]); // Keep last 10
    
    // Auto-clear inline feedback after 5 seconds
    setTimeout(() => {
      setCurrentFeedback(null);
    }, 5000);
  };
  
  const clearFeedback = () => {
    setCurrentFeedback(null);
  };
  
  return (
    <ActionFeedbackContext.Provider value={{ 
      currentFeedback, 
      feedbackHistory,
      showFeedback, 
      clearFeedback 
    }}>
      {children}
    </ActionFeedbackContext.Provider>
  );
};

export const useActionFeedback = () => {
  const context = useContext(ActionFeedbackContext);
  if (!context) {
    throw new Error('useActionFeedback must be used within ActionFeedbackProvider');
  }
  return context;
};

// =============================================================================
// FEEDBACK ICONS BY EVENT TYPE
// =============================================================================

const FEEDBACK_ICONS = {
  expenseLogged: Check,
  firstExpense: Star,
  numbersCompleted: TrendingUp,
  todoCompleted: Check,
  netWorthUpdated: TrendingUp,
  wealthLadderCompleted: Award,
  streakMaintained: Flame,
  stageUp: Zap,
  default: Check
};

const FEEDBACK_COLORS = {
  expenseLogged: '#22C55E',      // Green
  firstExpense: '#F59E0B',        // Amber
  numbersCompleted: '#0EA5E9',    // Sky blue
  todoCompleted: '#22C55E',       // Green
  netWorthUpdated: '#8B5CF6',     // Purple
  wealthLadderCompleted: '#A855F7', // Violet
  streakMaintained: '#F97316',    // Orange
  stageUp: '#EAB308',             // Yellow
  default: '#22C55E'
};

// =============================================================================
// TOAST FEEDBACK (Immediate Reinforcement)
// =============================================================================

export const showFeedbackToast = (feedback, eventType = 'default') => {
  const Icon = FEEDBACK_ICONS[eventType] || FEEDBACK_ICONS.default;
  const color = FEEDBACK_COLORS[eventType] || FEEDBACK_COLORS.default;
  
  // Main confirmation toast
  toast.success(
    <div className="flex items-center gap-3">
      <div 
        className="flex items-center justify-center w-8 h-8 rounded-full"
        style={{ backgroundColor: `${color}20` }}
      >
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div>
        <p className="font-medium text-gray-900">{feedback.confirmation}</p>
        <p className="text-sm text-gray-500">{feedback.reinforcement}</p>
      </div>
    </div>,
    {
      duration: 3000,
      className: 'action-feedback-toast'
    }
  );
  
  // Progress update toast (if progress changed)
  if (feedback.progressDelta > 0) {
    setTimeout(() => {
      toast(
        <div className="flex items-center gap-2">
          <ArrowUp className="w-4 h-4 text-green-500" />
          <span className="font-medium">{feedback.progressUpdate}</span>
        </div>,
        {
          duration: 2500,
          className: 'progress-toast'
        }
      );
    }, 500);
  }
  
  // Stage up special toast
  if (feedback.stageUpConfirmation) {
    setTimeout(() => {
      toast.success(
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-yellow-100">
            <Zap className="w-5 h-5 text-yellow-600" />
          </div>
          <div>
            <p className="font-bold text-lg text-gray-900">{feedback.stageUpConfirmation}</p>
            <p className="text-sm text-gray-600">{feedback.stageUpReinforcement}</p>
            {feedback.newJourneyStageTitle && (
              <p className="text-xs text-yellow-600 mt-1">
                New stage: {feedback.newJourneyStageTitle.en || feedback.newJourneyStageTitle}
              </p>
            )}
          </div>
        </div>,
        {
          duration: 5000,
          className: 'stage-up-toast'
        }
      );
    }, 1000);
  }
};

// =============================================================================
// INLINE FEEDBACK COMPONENT (Persistent Display)
// =============================================================================

export const ActionFeedbackInline = ({ className = '' }) => {
  const { currentFeedback, clearFeedback } = useActionFeedback();
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('es') ? 'es' : 'en';
  
  if (!currentFeedback) return null;
  
  const eventType = currentFeedback.eventType || 'default';
  const Icon = FEEDBACK_ICONS[eventType] || FEEDBACK_ICONS.default;
  const color = FEEDBACK_COLORS[eventType] || FEEDBACK_COLORS.default;
  
  return (
    <div 
      className={`relative overflow-hidden rounded-xl p-4 transition-all duration-300 animate-in slide-in-from-top ${className}`}
      style={{ 
        backgroundColor: `${color}10`,
        borderLeft: `4px solid ${color}`
      }}
      data-testid="action-feedback-inline"
    >
      <button 
        onClick={clearFeedback}
        className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
      >
        ×
      </button>
      
      <div className="flex items-start gap-3">
        <div 
          className="flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0"
          style={{ backgroundColor: `${color}20` }}
        >
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900">
            {currentFeedback.confirmation}
          </p>
          <p className="text-sm text-gray-600 mt-0.5">
            {currentFeedback.reinforcement}
          </p>
          
          {/* Progress bar */}
          {currentFeedback.newProgress !== undefined && (
            <div className="mt-3">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>{currentFeedback.progressUpdate}</span>
                {currentFeedback.progressDelta > 0 && (
                  <span className="text-green-600 font-medium flex items-center gap-1">
                    <ArrowUp className="w-3 h-3" />
                    +{currentFeedback.progressDelta}%
                  </span>
                )}
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full transition-all duration-500"
                  style={{ 
                    width: `${currentFeedback.newProgress}%`,
                    backgroundColor: color
                  }}
                />
              </div>
            </div>
          )}
          
          {/* Stage up celebration */}
          {currentFeedback.stageUpConfirmation && (
            <div className="mt-3 p-2 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-yellow-600" />
                <span className="font-semibold text-yellow-800">
                  {currentFeedback.stageUpConfirmation}
                </span>
              </div>
              <p className="text-sm text-yellow-700 mt-1">
                {currentFeedback.stageUpReinforcement}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// HELPER FUNCTION TO PROCESS API RESPONSE
// =============================================================================

export const processEventResponse = (response, eventType, showFeedbackFn) => {
  if (!response || !response.feedback) return;
  
  const feedback = {
    ...response.feedback,
    eventType,
    newProgress: response.progressPercent,
    journeyStage: response.journeyStage,
    journeyStageUp: response.journeyStageUp
  };
  
  // Show toast immediately
  showFeedbackToast(feedback, eventType);
  
  // Show inline feedback
  if (showFeedbackFn) {
    showFeedbackFn(feedback);
  }
  
  return feedback;
};

// =============================================================================
// COMPACT PROGRESS INDICATOR (For Dashboard Header)
// =============================================================================

export const ProgressIndicator = ({ 
  progressPercent, 
  journeyStage, 
  journeyStageTitle,
  color = '#22C55E',
  className = ''
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('es') ? 'es' : 'en';
  
  const stageTitle = journeyStageTitle?.[lang] || journeyStageTitle?.en || journeyStage;
  
  return (
    <div className={`flex items-center gap-3 ${className}`} data-testid="progress-indicator">
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm font-medium text-gray-700">
            {t('gamification.progress', 'Progress')}: {progressPercent}%
          </span>
          <span className="text-xs text-gray-500">
            {stageTitle} {t('gamification.stage', 'stage')}
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full rounded-full transition-all duration-500"
            style={{ 
              width: `${progressPercent}%`,
              backgroundColor: color
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ActionFeedbackInline;
